/**
 * Change Requests Monthly Charts Component (CR-6381)
 *
 * Displays stacked bar charts showing change request counts by type
 * (Date/Price/Qty) and initiator (Customer/Supplier) over a 12-month period.
 * Matches the old SupplierPortal Dashboard layout.
 * Bars are clickable to drill down to individual change requests.
 */
import { useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from 'recharts'
import type { ChangeRequestsMonthlyData } from '../../lib/api'

interface ChangeRequestsMonthlyChartsProps {
  data: ChangeRequestsMonthlyData
  onBarClick?: (month: string, changeType: 'date' | 'price' | 'qty', initiator?: 'customer' | 'supplier') => void
}

interface ChartData {
  month: string
  customer: number
  supplier: number
}

function ChangeRequestChart({
  title,
  data,
  changeType,
  onBarClick,
}: {
  title: string
  data: ChartData[]
  changeType: 'date' | 'price' | 'qty'
  onBarClick?: (month: string, changeType: 'date' | 'price' | 'qty', initiator?: 'customer' | 'supplier') => void
}) {
  const hasData = data.some((d) => d.customer > 0 || d.supplier > 0)

  const handleBarClick = useCallback(
    (barData: ChartData, initiator: 'customer' | 'supplier') => {
      if (onBarClick && barData[initiator] > 0) {
        onBarClick(barData.month, changeType, initiator)
      }
    },
    [onBarClick, changeType]
  )

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="h-[250px] flex items-center justify-center text-muted-foreground">
            No change request data available
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 10 }}
                interval={0}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip
                formatter={(value: number, name: string) => [
                  value,
                  name === 'supplier' ? 'Requested by Supplier' : 'Requested by Customer',
                ]}
                labelFormatter={(label) => `Month: ${label}`}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value) =>
                  value === 'supplier' ? 'Requested by Supplier' : 'Requested by Customer'
                }
              />
              <Bar
                dataKey="supplier"
                stackId="a"
                fill="#3b82f6"
                name="supplier"
                radius={[0, 0, 0, 0]}
                style={{ cursor: onBarClick ? 'pointer' : 'default' }}
                onClick={(barData) => handleBarClick(barData as unknown as ChartData, 'supplier')}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`supplier-${index}`}
                    className={entry.supplier > 0 && onBarClick ? 'hover:brightness-110' : ''}
                  />
                ))}
              </Bar>
              <Bar
                dataKey="customer"
                stackId="a"
                fill="#ef4444"
                name="customer"
                radius={[4, 4, 0, 0]}
                style={{ cursor: onBarClick ? 'pointer' : 'default' }}
                onClick={(barData) => handleBarClick(barData as unknown as ChartData, 'customer')}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`customer-${index}`}
                    className={entry.customer > 0 && onBarClick ? 'hover:brightness-110' : ''}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}

export function ChangeRequestsMonthlyCharts({ data, onBarClick }: ChangeRequestsMonthlyChartsProps) {
  const year = data.period.year

  return (
    <div className="space-y-4">
      {/* Full-width Date Changes Chart */}
      <ChangeRequestChart
        title={`Change Requests for Date - ${year}`}
        data={data.date_changes}
        changeType="date"
        onBarClick={onBarClick}
      />

      {/* Side-by-side Price and Qty Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChangeRequestChart
          title={`Change Requests for Price - ${year}`}
          data={data.price_changes}
          changeType="price"
          onBarClick={onBarClick}
        />
        <ChangeRequestChart
          title={`Change Requests for Qty - ${year}`}
          data={data.qty_changes}
          changeType="qty"
          onBarClick={onBarClick}
        />
      </div>
    </div>
  )
}
